import type { SetState } from "zustand";

import type { InterviewerHarnessType } from "@performyard/service-contracts";

import type { CompetencyFrameworkState, CompetencySuggestion, EditableCompetencyFields } from "../../types";
import type { AskPerformYardState } from "../types";

import { getActiveInterviewerHarness, setActiveInterviewerHarness } from "../helpers";

export type CompetencySlice = {
	competencyFramework: CompetencyFrameworkState | null;
	setCompetencySuggestions: (messageId: string, suggestions: CompetencySuggestion[]) => void;
	updateEditedCompetencyFields: (id: string, fields: Partial<EditableCompetencyFields>) => void;
	setCompetencyFrameworkStatus: (status: CompetencyFrameworkState["status"], errorMessage?: string | null) => void;
	clearCompetencyFramework: () => void;
	isCompetencyEditPanelOpen: boolean;
	openCompetencyEditPanel: () => void;
	closeCompetencyEditPanel: () => void;
	activeInterviewerHarness: InterviewerHarnessType | null;
	setActiveInterviewerHarness: (harness: InterviewerHarnessType | null) => void;
	deactivateInterviewerHarness: () => void;
};

export const createCompetencySlice = (set: SetState<AskPerformYardState>) => ({
	competencyFramework: null,
	isCompetencyEditPanelOpen: false,
	openCompetencyEditPanel: () => set({ isCompetencyEditPanelOpen: true }),
	closeCompetencyEditPanel: () => set({ isCompetencyEditPanelOpen: false }),
	activeInterviewerHarness: getActiveInterviewerHarness(),

	setActiveInterviewerHarness: (harness: InterviewerHarnessType | null) => {
		setActiveInterviewerHarness(harness);
		set({ activeInterviewerHarness: harness });
	},

	deactivateInterviewerHarness: () => {
		setActiveInterviewerHarness(null);
		set({ activeInterviewerHarness: null });
	},
	setCompetencySuggestions: (messageId, suggestions) => {
		set((state) => {
			const framework = state.competencyFramework;
			const isSameMessage = framework && framework.messageId === messageId;
			const hasUnsavedEdits = framework && Object.keys(framework.editedFields).length > 0;

			const shouldPreserve = isSameMessage && hasUnsavedEdits;

			return {
				messages: state.messages.map((message) =>
					message.id === messageId ? { ...message, competencySuggestions: suggestions } : message
				),
				...(shouldPreserve
					? {}
					: {
							competencyFramework: {
								suggestions,
								editedFields: {},
								status: "pending" as const,
								errorMessage: null,
								messageId,
							},
						}),
			};
		});
	},

	updateEditedCompetencyFields: (id, fields) => {
		set((state) => {
			if (!state.competencyFramework) return state;
			return {
				competencyFramework: {
					...state.competencyFramework,
					editedFields: {
						...state.competencyFramework.editedFields,
						[id]: { ...state.competencyFramework.editedFields[id], ...fields },
					},
				},
			};
		});
	},

	setCompetencyFrameworkStatus: (status, errorMessage: string | null = null) => {
		set((state) => {
			if (!state.competencyFramework) return state;
			return {
				competencyFramework: {
					...state.competencyFramework,
					status,
					errorMessage,
				},
			};
		});
	},

	clearCompetencyFramework: () => set({ competencyFramework: null }),
});
